import { noop } from 'lodash';
import axios from 'axios';
import { PromptMomentNotification } from 'google-one-tap';
import { logger } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import pathfinder from '@fiverr-private/pathfinder';
import { GOOGLE_AUTH_SDK, GOOGLE_ONE_TAP_PARENT_CONTAINER_ID } from '../../shared/constants/googleOneTap';
import { AuthenticationResponse } from '../../shared/constants/authenticationFlow';
import {
    GoogleOneTapCallbackParams,
    InitGoogleOneTapParams,
    LoadScriptParams,
    LoadSdkParams,
} from './googleOneTap.types';

const loadScript = ({ id, src, onLoad = noop, onError = noop }: LoadScriptParams) => {
    const scriptElement = document.createElement('script');

    scriptElement.id = id;
    scriptElement.src = src;
    scriptElement.async = true;
    scriptElement.defer = true;

    scriptElement.addEventListener('load', onLoad);
    scriptElement.addEventListener('error', onError);

    document.body.appendChild(scriptElement);
};

const loadSdk = async ({ onSdkLoad, onSdkLoadError }: LoadSdkParams) =>
    new Promise(() => {
        loadScript({
            id: 'google-one-tap-sdk',
            src: GOOGLE_AUTH_SDK,
            onLoad: onSdkLoad,
            onError: onSdkLoadError,
        });
    });

const eligibleForExperience = () => {
    const { userId, isMobile, queryParameters } = getContext();
    const hasInvitationToken = !!queryParameters?.token;

    return !userId && !isMobile && !hasInvitationToken;
};

export const loadGoogleOneTapClient = async (googleClientId, onSdkLoadSuccess, onSdkLoadError) => {
    if (!googleClientId || !eligibleForExperience()) {
        return;
    }

    await loadSdk({ onSdkLoad: onSdkLoadSuccess, onSdkLoadError });
};

export const initGoogleOneTap = ({
    onOneTapCallback,
    onOneTapSuccess,
    onOneTapError,
    googleClientId,
    onNotificationReceived,
}: InitGoogleOneTapParams) => {
    if (typeof google?.accounts === 'undefined') {
        return;
    }

    try {
        google.accounts.id.initialize({
            client_id: googleClientId ?? '',
            cancel_on_tap_outside: false,
            callback: (credentialResponse) =>
                googleOneTapCallback({ credentialResponse, onOneTapCallback, onOneTapSuccess, onOneTapError }),
            prompt_parent_id: GOOGLE_ONE_TAP_PARENT_CONTAINER_ID,
        });

        promptGoogleOneTap(onNotificationReceived);
    } catch (error) {
        logger.warn('google one tap - initialization error');
    }
};
const promptGoogleOneTap = (
    onNotificationReceived: (notification: PromptMomentNotification) => void = googleOneTapMomentCallback
) => {
    google.accounts.id.prompt((notification) => {
        onNotificationReceived(notification);
    });
};

const googleOneTapMomentCallback = (notification: PromptMomentNotification) => {
    logger.warn('google one tap - notification received', notification);
};

export const googleOneTapCallback = async ({
    credentialResponse,
    onOneTapCallback,
    onOneTapSuccess,
    onOneTapError,
}: GoogleOneTapCallbackParams) => {
    onOneTapCallback?.();

    const url = pathfinder('auth_google_one_tap');

    try {
        const { data: authenticationResponse } = await axios.post(url, { credential: credentialResponse.credential });
        const { result_hash: result } = authenticationResponse as AuthenticationResponse;

        if (result.status === 'success') {
            onOneTapSuccess?.(authenticationResponse);
            return;
        }

        if (result.status === 'failure') {
            onOneTapError?.(authenticationResponse);
        }
    } catch (error) {
        logger.warn('google one tap - callback error');
    }
};
